import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { verifyOtp, forgotPassword } from "../ApiHelper/url_Helpers";
import { RiseLoader } from "react-spinners";
import { toasterSuccess, toasterError } from "./toaster";
import "./loader.css";

export const VerifyOtp = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const email = sessionStorage.getItem("resetEmail");

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      setError("Please enter the full 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await verifyOtp({ email, otp: otpValue });
      const token = response.reset_token || response.token;
      sessionStorage.setItem("otpVerified", "true");
      localStorage.setItem('userToken', token);
      sessionStorage.setItem('userToken', token);
      toasterSuccess("OTP Verified successfully");
      navigate("/reset-password");
    } catch (err) {
      setError(err?.message || "Invalid or expired OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;

    setResending(true);
    setError("");
    try {
      await forgotPassword({ email });
      toasterSuccess("OTP resent successfully");
      setTimer(60);
    } catch (err) {
      setError(err?.message || "Failed to resend OTP");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row items-center justify-center bg-white p-4 md:gap-x-14">
      <div className="w-full md:w-1/2 flex justify-center md:justify-end items-center mb-10 md:mb-0 md:pr-4">
        <div className="relative max-w-[500px]">
          <img
            src="/cuate.png"
            alt="Verify OTP Illustration"
            className="w-full h-auto"
          />
        </div>
      </div>

      <div className="w-full md:w-1/2 flex justify-center md:justify-start md:pl-4">
        <div className="w-full max-w-[450px] bg-[#f0f9ff] rounded-[32px] p-5 md:p-8 border border-gray-300">
          <div className="mb-5">
            <Link
              to="/forgot-password"
              className="flex items-center text-sm font-semibold text-[#1a2333] hover:underline mb-4"
            >
              <ArrowLeft size={16} className="mr-1" /> Back to Forgot Password
            </Link>
            <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2">
              Verify OTP
            </h1>
            <p className="text-gray-500 text-lg">
              Check your email for the 6-digit verification code.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <p className="text-red-500 text-sm mb-4 bg-red-50 p-2 rounded-lg text-center font-medium">
                {error}
              </p>
            )}

            <div className="flex justify-between items-center mb-10 gap-2">
              {otp.map((data, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  required
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={data}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all text-[#1a2333]"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#1a2333] hover:bg-[#252f44] text-white font-semibold py-3 rounded-xl transition-all shadow-md mb-6 ${loading ? "opacity-70" : "active:scale-[0.99]"}`}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            <div className="text-center">
              <span className="text-gray-500">Didn't receive the code? </span>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={timer > 0 || resending}
                className={`font-semibold text-[#1a2333] hover:underline disabled:text-gray-400 disabled:no-underline`}
              >
                {resending ? "Resending..." : timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
              </button>
            </div>
          </form>

          {(loading || resending) && (
            <div className="loader-col">
              <RiseLoader color="#35e6e9" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
